import { Youtube, Twitch, Instagram, Facebook, Twitter, Music2, Zap } from "lucide-react";
import { SEO } from "@/lib/seo";
import { PageHero } from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import { SectionLabel } from "@/components/common/SectionLabel";
import { MEDIA } from "@/config/media";

const SERVICES = [
  "Live Match Commentary",
  "Grassroots Women's Football",
  "Matchday Coverage",
  "Interviews",
  "Football Content",
];

const SOCIALS = [
  { id: "youtube", Icon: Youtube },
  { id: "twitch", Icon: Twitch },
  { id: "kick", Icon: Zap },
  { id: "tiktok", Icon: Music2 },
  { id: "instagram", Icon: Instagram },
  { id: "x", Icon: Twitter },
  { id: "facebook", Icon: Facebook },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About The Commentator"
        description="Meet the commentator behind WomensMatchDayLive — bringing live commentary and coverage to grassroots women's football across the UK."
        path="/about"
        jsonLd={{ "@context": "https://schema.org", "@type": "Person", name: "[CLIENT NAME]", jobTitle: "Women's Football Commentator" }}
      />
      <PageHero eyebrow="About" title="About WomensMatchDayLive" subtitle="Text goes here" crumbLabel="About" />

      <section className="bg-white py-20">
        <div className="mx-auto max-w-3xl px-6 lg:px-12">
          <Reveal>
            <h2 className="font-heading text-4xl uppercase text-brand-black">About WomensMatchDayLive</h2>
            <p className="mt-5 text-base leading-relaxed text-black/60">
              Hello and welcome to Women&apos;s Matchday Live, a page dedicated to the FaWNL.
            </p>
            <p className="mt-4 text-base leading-relaxed text-black/60">
              With live shows available to you every Sunday from 2pm, coming to you live from a new ground each week, streamable on Twitch, Kick and YouTube, we bring you all the action across the FaWNL including live goal updates, major moments and live commentary of the game.
            </p>
            <p className="mt-4 text-base leading-relaxed text-black/60">
              Follow us on all socials at @WomensMatchdayLive to keep up to date on exclusive content as it comes out.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-surface-light py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <Reveal>
            <SectionLabel index="01" title="What We Do" />
            <h2 className="mt-3 font-heading text-4xl uppercase text-brand-black sm:text-5xl">What We Do</h2>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((service, i) => (
              <Reveal key={service} delay={i * 0.06} className="border border-border-light bg-white p-7">
                <h3 className="font-heading text-2xl uppercase text-brand-black">{service}</h3>
                <p className="mt-2 text-sm text-black/60">Text goes here</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-brand-black py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2 lg:px-12">
          <Reveal>
            <SectionLabel index="02" title="Why Grassroots" />
            <h2 className="mt-3 font-heading text-4xl uppercase text-white sm:text-5xl">Why Women&apos;s Grassroots Football?</h2>
            <p className="mt-5 text-white/60">Text goes here</p>
          </Reveal>
          <Reveal delay={0.1} className="relative aspect-[4/3] overflow-hidden border border-border-dark">
            <img src={MEDIA.aboutAction.url} alt={MEDIA.aboutAction.alt} loading="lazy" className="h-full w-full object-cover" />
          </Reveal>
        </div>
      </section>

      <section className="bg-surface-dark py-24">
        <div className="mx-auto max-w-5xl px-6 lg:px-12">
          <Reveal>
            <SectionLabel index="03" title="Commentator Profile" />
          </Reveal>
          <Reveal delay={0.1} className="mt-8 grid gap-10 border border-border-dark bg-brand-black p-8 md:grid-cols-[220px_1fr] md:p-12">
            <div className="aspect-square w-full overflow-hidden border border-border-dark">
              <img src={MEDIA.commentatorProfile.url} alt={MEDIA.commentatorProfile.alt} loading="lazy" className="h-full w-full object-cover" />
            </div>
            <div>
              <h3 className="font-heading text-3xl uppercase text-white sm:text-4xl">[CLIENT NAME]</h3>
              <p className="mt-1 font-subheading text-sm uppercase tracking-wide text-brand-purple">Women&apos;s Football Commentator</p>
              <p className="mt-4 text-white/60">Text goes here</p>
              <p className="mt-3 text-sm text-white/40">Experience: Text goes here</p>
              <div className="mt-5 flex flex-wrap gap-3">
                {SOCIALS.map(({ id, Icon }) => (
                  <span key={id} data-testid={`profile-social-${id}`} className="inline-flex h-9 w-9 items-center justify-center border border-border-dark text-white/50">
                    <Icon className="h-4 w-4" />
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
